/**
 * <video-stories> — Cartes vidéo (aperçus autoplay) + viewer plein écran type "story".
 *
 * Autonome : aucune dépendance au reste du thème.
 * Perf : les aperçus utilisent preload="none" + poster, et ne sont lus que
 * lorsqu'ils sont visibles (IntersectionObserver), puis mis en pause hors écran.
 * Respecte prefers-reduced-motion (pas d'autoplay d'aperçu).
 *
 * Attributs (data-*) posés par block-video-stories.liquid :
 *  - data-sound-on   : "true" | "false"  -> son activé dans le viewer plein écran
 *  - data-auto-advance : "true" | "false" -> passe à la vidéo suivante en fin de lecture
 */
class VideoStories extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.vs-card'))
    if (!this.cards.length) return

    this.soundOn = this.dataset.soundOn !== 'false'
    this.autoAdvance = this.dataset.autoAdvance !== 'false'
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.viewerOpen = false
    this.currentIndex = 0
    this.visiblePreviews = new Set()

    this.setupPreviews()
    this.bindCardTriggers()
  }

  disconnectedCallback() {
    this.observer?.disconnect()
    if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown)
    this.overlay?.remove()
    document.documentElement.classList.remove('vs-scroll-lock')
  }

  /* ---------- Aperçus (cartes) ---------- */

  setupPreviews() {
    const autoplayVideos = this.querySelectorAll('video[data-autoplay-preview]')
    if (!autoplayVideos.length || this.reducedMotion || !('IntersectionObserver' in window)) return

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target
          if (entry.isIntersecting) {
            this.visiblePreviews.add(video)
            if (!this.viewerOpen) this.playPreview(video)
          } else {
            this.visiblePreviews.delete(video)
            video.pause()
          }
        })
      },
      { threshold: 0.5 }
    )

    autoplayVideos.forEach((video) => this.observer.observe(video))
  }

  playPreview(video) {
    // preload="none" -> la source n'est réellement téléchargée qu'ici
    const promise = video.play()
    if (promise && typeof promise.catch === 'function') promise.catch(() => {})
  }

  bindCardTriggers() {
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (event) => {
        event.preventDefault()
        this.openViewer(index)
      })
    })
  }

  /* ---------- Viewer plein écran ---------- */

  buildOverlay() {
    const overlay = document.createElement('div')
    overlay.className = 'vs-overlay'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-label', 'Vidéos')

    const segments = this.cards
      .map(() => '<span class="vs-progress__seg"><span class="vs-progress__fill"></span></span>')
      .join('')

    overlay.innerHTML = `
      <div class="vs-overlay__backdrop" data-vs-close></div>
      <div class="vs-overlay__stage">
        <div class="vs-progress">${segments}</div>
        <button type="button" class="vs-overlay__close" data-vs-close aria-label="Fermer">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <video class="vs-overlay__video" playsinline></video>
        <p class="vs-overlay__title"></p>
        <button type="button" class="vs-nav vs-nav--prev" aria-label="Précédent">
          <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button type="button" class="vs-nav vs-nav--next" aria-label="Suivant">
          <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>`

    document.body.appendChild(overlay)

    this.overlay = overlay
    this.stage = overlay.querySelector('.vs-overlay__stage')
    this.viewerVideo = overlay.querySelector('.vs-overlay__video')
    this.viewerTitle = overlay.querySelector('.vs-overlay__title')
    this.segFills = Array.from(overlay.querySelectorAll('.vs-progress__fill'))
    this.prevBtn = overlay.querySelector('.vs-nav--prev')
    this.nextBtn = overlay.querySelector('.vs-nav--next')

    this.viewerVideo.muted = !this.soundOn

    overlay.querySelectorAll('[data-vs-close]').forEach((el) => el.addEventListener('click', () => this.closeViewer()))
    this.prevBtn.addEventListener('click', () => this.prev())
    this.nextBtn.addEventListener('click', () => this.next())

    // Tap sur la vidéo : pause / lecture
    this.viewerVideo.addEventListener('click', () => {
      this.viewerVideo.paused ? this.viewerVideo.play().catch(() => {}) : this.viewerVideo.pause()
    })

    // Progression + avance auto
    this.viewerVideo.addEventListener('timeupdate', () => this.updateProgress())
    this.viewerVideo.addEventListener('ended', () => {
      this.fillCurrentSegment()
      if (this.autoAdvance) this.next(true)
    })

    // Swipe tactile
    let startX = 0
    let startY = 0
    this.stage.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
      },
      { passive: true }
    )
    this.stage.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - startX
        const dy = e.changedTouches[0].clientY - startY
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          dx < 0 ? this.next() : this.prev()
        } else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) {
          this.closeViewer() // swipe vers le bas = fermer
        }
      },
      { passive: true }
    )

    this.onKeydown = (e) => {
      if (!this.viewerOpen) return
      if (e.key === 'Escape') this.closeViewer()
      else if (e.key === 'ArrowRight') this.next()
      else if (e.key === 'ArrowLeft') this.prev()
      else if (e.key === 'Tab') this.trapFocus(e)
    }
    document.addEventListener('keydown', this.onKeydown)
  }

  openViewer(index) {
    if (!this.overlay) this.buildOverlay()

    this.viewerOpen = true
    this.lastFocused = document.activeElement
    this.visiblePreviews.forEach((video) => video.pause())

    document.documentElement.classList.add('vs-scroll-lock')
    this.overlay.classList.add('is-open')
    this.goTo(index)

    requestAnimationFrame(() => this.overlay.querySelector('.vs-overlay__close').focus())
  }

  closeViewer() {
    if (!this.viewerOpen) return
    this.viewerOpen = false

    this.viewerVideo.pause()
    this.viewerVideo.removeAttribute('src')
    this.viewerVideo.innerHTML = ''
    this.viewerVideo.load()

    this.overlay.classList.remove('is-open')
    document.documentElement.classList.remove('vs-scroll-lock')

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus()

    // Reprend les aperçus visibles
    if (!this.reducedMotion) this.visiblePreviews.forEach((video) => this.playPreview(video))
  }

  goTo(index) {
    if (index < 0 || index >= this.cards.length) return
    this.currentIndex = index

    const card = this.cards[index]
    const sources = card.querySelectorAll('video source')

    this.viewerVideo.pause()
    this.viewerVideo.innerHTML = ''
    sources.forEach((source) => {
      const clone = document.createElement('source')
      clone.src = source.getAttribute('src')
      if (source.getAttribute('type')) clone.type = source.getAttribute('type')
      this.viewerVideo.appendChild(clone)
    })

    const poster = card.querySelector('video')?.getAttribute('poster')
    if (poster) this.viewerVideo.setAttribute('poster', poster)

    this.viewerVideo.muted = !this.soundOn
    this.viewerVideo.loop = false
    this.viewerVideo.load()
    this.viewerVideo.play().catch(() => {
      // Autoplay avec son parfois bloqué : on retombe en muet
      this.viewerVideo.muted = true
      this.viewerVideo.play().catch(() => {})
    })

    this.viewerTitle.textContent = card.dataset.title || ''
    this.resetProgress()
    this.prevBtn.hidden = index === 0
    this.nextBtn.hidden = index === this.cards.length - 1
  }

  next(fromEnded) {
    if (this.currentIndex < this.cards.length - 1) this.goTo(this.currentIndex + 1)
    else if (fromEnded) this.closeViewer()
  }

  prev() {
    if (this.currentIndex > 0) this.goTo(this.currentIndex - 1)
  }

  /* ---------- Barre de progression ---------- */

  resetProgress() {
    this.segFills.forEach((fill, i) => {
      fill.style.width = i < this.currentIndex ? '100%' : '0%'
    })
  }

  updateProgress() {
    const fill = this.segFills[this.currentIndex]
    if (!fill || !this.viewerVideo.duration) return
    fill.style.width = (this.viewerVideo.currentTime / this.viewerVideo.duration) * 100 + '%'
  }

  fillCurrentSegment() {
    const fill = this.segFills[this.currentIndex]
    if (fill) fill.style.width = '100%'
  }

  /* ---------- A11y ---------- */

  trapFocus(event) {
    const focusable = this.overlay.querySelectorAll('button:not([hidden])')
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

if (!customElements.get('video-stories')) {
  customElements.define('video-stories', VideoStories)
}